import { useState } from 'react';
import { addDoc, collection, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { Eye, EyeOff, Pencil, Plus, Trash2 } from 'lucide-react';
import { db } from '../../lib/firebase';
import { useAdminCollection } from './hooks';
import { EmptyState, Field, Modal } from './adminUi';
import ServiceIcon, { SERVICE_ICON_KEYS } from '../../components/ServiceIcon';
import type { CustomService } from '../../lib/types';
import { money } from '../../lib/format';

const empty: Omit<CustomService, 'id'> = {
  name: '',
  description: '',
  icon: 'wrench',
  features: [],
  priceFrom: null,
  comingSoon: false,
  active: true,
  order: 99,
};

export default function ServicesAdmin() {
  const { items, loading } = useAdminCollection<CustomService>('customServices');
  const [editing, setEditing] = useState<Partial<CustomService> | null>(null);
  const [saving, setSaving] = useState(false);

  const sorted = [...items].sort((a, b) => a.order - b.order);

  const save = async () => {
    if (!editing?.name) return;
    setSaving(true);
    const { id, ...data } = { ...empty, ...editing };
    try {
      if (id) await updateDoc(doc(db(), 'customServices', id), data as any);
      else await addDoc(collection(db(), 'customServices'), data);
      setEditing(null);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (s: CustomService) => {
    if (confirm(`Delete "${s.name}"?`)) await deleteDoc(doc(db(), 'customServices', s.id));
  };

  return (
    <div className="mx-auto max-w-5xl">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold text-white">Custom Services</h1>
          <p className="mt-1 text-sm text-zinc-500">
            Audio, lighting, wraps, PPF — the services shown on the Custom Work page. Mark future
            offerings "coming soon" until you're ready.
          </p>
        </div>
        <button className="btn-primary !py-2.5 text-sm" onClick={() => setEditing({ ...empty })}>
          <Plus size={16} /> New Service
        </button>
      </div>

      {loading ? (
        <EmptyState text="Loading…" />
      ) : sorted.length === 0 ? (
        <EmptyState text="No services in the database yet — publish starter content from the Dashboard or add one." />
      ) : (
        <div className="mt-6 grid gap-4 md:grid-cols-2">
          {sorted.map((s) => (
            <div key={s.id} className={`card p-5 ${!s.active ? 'opacity-50' : ''}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/[0.06] text-accent-400">
                  <ServiceIcon name={s.icon} size={20} />
                </div>
                <div className="flex gap-1.5">
                  <button className="glass rounded-lg p-2 text-zinc-300 hover:text-white" title={s.active ? 'Hide' : 'Show'} onClick={() => updateDoc(doc(db(), 'customServices', s.id), { active: !s.active })}>
                    {s.active ? <Eye size={15} /> : <EyeOff size={15} />}
                  </button>
                  <button className="glass rounded-lg p-2 text-zinc-300 hover:text-accent-300" onClick={() => setEditing(s)}>
                    <Pencil size={15} />
                  </button>
                  <button className="glass rounded-lg p-2 text-zinc-300 hover:text-red-400" onClick={() => remove(s)}>
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
              <h3 className="mt-3 font-display font-bold text-white">
                {s.name}
                {s.comingSoon && (
                  <span className="ml-2 rounded-full bg-amber-400/15 px-2 py-0.5 text-[10px] font-bold uppercase text-amber-300">Coming Soon</span>
                )}
              </h3>
              <p className="mt-1 text-sm text-zinc-400">{s.description}</p>
              <p className="mt-2 text-sm font-semibold text-accent-300">
                {s.priceFrom != null ? `From ${money(s.priceFrom)}` : 'Custom quoted'}
              </p>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <Modal title={editing.id ? `Edit ${editing.name}` : 'New Service'} onClose={() => setEditing(null)}>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Name" span2>
              <input className="input" value={editing.name ?? ''} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
            </Field>
            <Field label="Description" span2>
              <textarea className="input min-h-20" value={editing.description ?? ''} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
            </Field>
            <Field label="Icon">
              <select className="input" value={editing.icon ?? 'wrench'} onChange={(e) => setEditing({ ...editing, icon: e.target.value })}>
                {SERVICE_ICON_KEYS.map((k) => (
                  <option key={k} value={k}>{k}</option>
                ))}
              </select>
            </Field>
            <Field label='Starting price ($, blank = "custom quoted")'>
              <input
                className="input"
                type="number"
                min={0}
                value={editing.priceFrom ?? ''}
                onChange={(e) => setEditing({ ...editing, priceFrom: e.target.value === '' ? null : Number(e.target.value) })}
              />
            </Field>
            <Field label="What's included (one per line)" span2>
              <textarea
                className="input min-h-28"
                value={(editing.features ?? []).join('\n')}
                onChange={(e) => setEditing({ ...editing, features: e.target.value.split('\n').filter((l) => l.trim() !== '') })}
              />
            </Field>
            <Field label="Display order">
              <input className="input" type="number" value={editing.order ?? 99} onChange={(e) => setEditing({ ...editing, order: Number(e.target.value) })} />
            </Field>
            <div className="flex items-end gap-5 pb-1">
              <label className="flex items-center gap-2 text-sm text-zinc-300">
                <input type="checkbox" className="h-4 w-4 accent-sky-500" checked={editing.comingSoon ?? false} onChange={(e) => setEditing({ ...editing, comingSoon: e.target.checked })} />
                Coming soon
              </label>
              <label className="flex items-center gap-2 text-sm text-zinc-300">
                <input type="checkbox" className="h-4 w-4 accent-sky-500" checked={editing.active ?? true} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} />
                Visible
              </label>
            </div>
          </div>
          <div className="mt-6 flex justify-end gap-3">
            <button className="btn-ghost !py-2.5 text-sm" onClick={() => setEditing(null)}>Cancel</button>
            <button className="btn-primary !py-2.5 text-sm" onClick={save} disabled={saving || !editing.name}>
              {saving ? 'Saving…' : 'Save Service'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
